import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, MapPin, Building2, Calendar, ExternalLink, 
  Bookmark, BookmarkCheck, Share2, Clock, DollarSign, CheckCircle2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkStatus, setBookmarkStatus] = useState("saved");

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await fetch(`${API}/jobs/${jobId}`);
        if (response.ok) {
          const data = await response.json();
          setJob(data);
        } else {
          toast.error("Job not found");
          navigate("/jobs");
        }
      } catch (error) {
        console.error("Error fetching job:", error);
        toast.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    const checkBookmark = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${API}/bookmarks/check/${jobId}`, {
          credentials: "include"
        });
        if (response.ok) {
          const data = await response.json();
          setIsBookmarked(data.is_bookmarked);
          if (data.bookmark) {
            setBookmarkStatus(data.bookmark.status);
          }
        }
      } catch (error) {
        console.error("Error checking bookmark:", error);
      }
    };

    fetchJob();
    checkBookmark();
  }, [jobId, user, navigate]);

  const toggleBookmark = async () => {
    if (!user) {
      toast.error("Please login to bookmark jobs");
      return;
    }

    try {
      if (isBookmarked) {
        await fetch(`${API}/bookmarks/${jobId}`, {
          method: "DELETE",
          credentials: "include"
        });
        setIsBookmarked(false);
        toast.success("Bookmark removed");
      } else {
        await fetch(`${API}/bookmarks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ job_id: jobId })
        });
        setIsBookmarked(true);
        setBookmarkStatus("saved");
        toast.success("Job bookmarked!");
      }
    } catch (error) {
      toast.error("Failed to update bookmark");
    }
  };

  const shareJob = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: job?.title,
          text: `Check out this postdoc position: ${job?.title} at ${job?.institution}`,
          url
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const deadlineDate = job.deadline ? new Date(job.deadline) : null;
  const isDeadlineSoon = deadlineDate && (deadlineDate - new Date()) / (1000 * 60 * 60 * 24) < 14;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link 
          to="/jobs" 
          className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-jobs-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block px-4 py-1.5 bg-[#800020]/20 text-[#D4AF37] text-sm font-semibold uppercase tracking-wide rounded-sm">
                {job.field}
              </span>
              {isDeadlineSoon && deadlineDate && (
                <span className="inline-block px-3 py-1 bg-red-500/10 text-red-400 text-xs font-semibold rounded-sm">
                  Deadline Soon
                </span>
              )}
            </div>

            <h1 className="font-serif text-3xl md:text-4xl font-medium text-white mb-4" data-testid="job-title">
              {job.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-6">
              <span className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {job.institution}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {job.location}, {job.country}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={toggleBookmark}
                variant={isBookmarked ? "default" : "outline"}
                className={isBookmarked 
                  ? "bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-sm"
                  : "border-white/20 text-white hover:bg-white/5 rounded-sm"
                }
                data-testid="job-detail-bookmark-btn"
              >
                {isBookmarked ? (
                  <>
                    <BookmarkCheck className="w-4 h-4 mr-2" />
                    Saved
                  </>
                ) : (
                  <>
                    <Bookmark className="w-4 h-4 mr-2" />
                    Save Position
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={shareJob}
                className="border-white/20 text-white hover:bg-white/5 rounded-sm"
                data-testid="job-detail-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              {job.source_url && (
                <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-[#800020] text-white hover:bg-[#600018] rounded-sm"
                    data-testid="job-detail-apply-btn"
                  >
                    Apply Now
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          </header>

          {/* Meta Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Building2, label: "Department", value: job.department },
              { icon: MapPin, label: "Location", value: `${job.location}, ${job.country}` },
              { icon: DollarSign, label: "Salary", value: job.salary_range || "Not specified" },
              { icon: Calendar, label: "Deadline", value: deadlineDate?.toLocaleDateString() || "Open" },
            ].map((item) => (
              <div 
                key={item.label}
                className="p-4 rounded-sm bg-[#0A0A0A] border border-white/10"
              >
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <item.icon className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">{item.label}</span>
                </div>
                <p className="text-white font-medium text-sm">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-semibold text-white mb-4">About the Position</h2>
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-300 leading-relaxed whitespace-pre-line" data-testid="job-description">
                {job.description}
              </p>
            </div>
          </section>

          {/* Requirements */}
          {job.requirements && job.requirements.length > 0 && (
            <section className="mb-8">
              <h2 className="font-serif text-2xl font-semibold text-white mb-4">Requirements</h2>
              <ul className="space-y-3">
                {job.requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{req}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Source Info */}
          <section className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Source</p>
                <p className="text-gray-300">{job.source === "scraped" ? "Academic Job Board" : job.source}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Posted</p>
                <p className="text-gray-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {new Date(job.posted_date).toLocaleDateString()}
                </p>
              </div>
              {job.source_url && (
                <div>
                  <a 
                    href={job.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline text-sm"
                  >
                    View Original Listing
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          </section>
        </motion.article>
      </div>
    </div>
  );
}
